/**
 * Redux-Saga for Data Categorization
 *
 * Handles all asynchronous operations including:
 * - API calls
 * - Side effects
 * - Async workflows
 *
 * @author Development Team
 * @version 2.0
 */

import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import * as dataCategorizationService from '../service/dataCategorizationService';

// ==================== CATEGORIES SAGAS ====================

/**
 * Worker Saga: Fetch all categories
 */
function* fetchAllCategoriesSaga(action) {
  try {
    const { page = 0, size = 20, sortBy = 'createdAt', sortOrder = 'DESC' } = action.payload || {};

    const response = yield call(
      dataCategorizationService.getAllIncidentCategories,
      page,
      size,
      sortBy,
      sortOrder
    );

    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch categories';
    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Fetch single category
 */
function* fetchCategoryByIdSaga(action) {
  try {
    const { id } = action.payload;
    const response = yield call(dataCategorizationService.getIncidentCategoryById, id);

    yield put({
      type: actionTypes.GET_DATA_CATEGORY_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch category';
    yield put({
      type: actionTypes.GET_DATA_CATEGORY_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Create category
 */
function* createCategorySaga(action) {
  try {
    const categoryData = action.payload;
    const response = yield call(dataCategorizationService.createIncidentCategory, categoryData);

    yield put({
      type: actionTypes.CREATE_DATA_CATEGORY_SUCCESS,
      payload: response.data
    });

    // Fetch updated list
    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES,
      payload: {}
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to create category';
    yield put({
      type: actionTypes.CREATE_DATA_CATEGORY_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Update category
 */
function* updateCategorySaga(action) {
  try {
    const { id, ...categoryData } = action.payload;
    const response = yield call(dataCategorizationService.updateIncidentCategory, id, categoryData);

    yield put({
      type: actionTypes.UPDATE_DATA_CATEGORY_SUCCESS,
      payload: response.data
    });

    // Fetch updated list
    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES,
      payload: {}
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to update category';
    yield put({
      type: actionTypes.UPDATE_DATA_CATEGORY_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Delete category
 */
function* deleteCategorySaga(action) {
  try {
    const { id } = action.payload;
    yield call(dataCategorizationService.deleteIncidentCategory, id);

    yield put({
      type: actionTypes.DELETE_DATA_CATEGORY_SUCCESS,
      payload: id
    });

    // Fetch updated list
    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES,
      payload: {}
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to delete category';
    yield put({
      type: actionTypes.DELETE_DATA_CATEGORY_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Change category status
 */
function* changeStatusSaga(action) {
  try {
    const { id, status } = action.payload;
    const response = yield call(dataCategorizationService.changeCategoryStatus, id, status);

    yield put({
      type: actionTypes.CHANGE_CATEGORY_STATUS_SUCCESS,
      payload: response.data
    });

    // Fetch updated list
    yield put({
      type: actionTypes.GET_ALL_DATA_CATEGORIES,
      payload: {}
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to change status';
    yield put({
      type: actionTypes.CHANGE_CATEGORY_STATUS_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Search categories
 */
function* searchCategoriesSaga(action) {
  try {
    const searchParams = action.payload;
    const response = yield call(dataCategorizationService.searchCategories, searchParams);

    yield put({
      type: actionTypes.SEARCH_CATEGORIES_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to search categories';
    yield put({
      type: actionTypes.SEARCH_CATEGORIES_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Get audit log
 */
function* getAuditLogSaga(action) {
  try {
    const { id, page = 0, size = 10 } = action.payload;
    const response = yield call(dataCategorizationService.getCategoryAuditLog, id, page, size);

    yield put({
      type: actionTypes.GET_AUDIT_LOG_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    const errorMessage = error.message || 'Failed to fetch audit log';
    yield put({
      type: actionTypes.GET_AUDIT_LOG_ERROR,
      payload: errorMessage
    });
  }
}

// ==================== RULES MANAGEMENT SAGAS ====================

/**
 * Worker Saga: Fetch all rules
 */
function* getAllRulesSaga(action) {
  try {
    const params = action.payload || {};
    const response = yield call(dataCategorizationService.getAllRules, params);

    yield put({
      type: actionTypes.GET_ALL_RULES_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_ALL_RULES_ERROR,
      payload: error.message || 'Failed to fetch rules'
    });
  }
}

/**
 * Worker Saga: Get rule by ID
 */
function* getRuleByIdSaga(action) {
  try {
    const { ruleId } = action.payload;
    const response = yield call(dataCategorizationService.getRuleById, ruleId);

    yield put({
      type: actionTypes.GET_RULE_BY_ID_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_RULE_BY_ID_ERROR,
      payload: error.message || 'Failed to fetch rule'
    });
  }
}

/**
 * Worker Saga: Create rule
 */
function* createRuleSaga(action) {
  try {
    const ruleData = action.payload;
    const response = yield call(dataCategorizationService.createRule, ruleData);

    yield put({
      type: actionTypes.CREATE_RULE_SUCCESS,
      payload: response.data
    });

    // Refresh rules list
    yield put({ type: actionTypes.GET_ALL_RULES, payload: {} });
  } catch (error) {
    yield put({
      type: actionTypes.CREATE_RULE_ERROR,
      payload: error.message || 'Failed to create rule'
    });
  }
}

/**
 * Worker Saga: Update rule
 */
function* updateRuleSaga(action) {
  try {
    const { ruleId, ...ruleData } = action.payload;
    const response = yield call(dataCategorizationService.updateRule, ruleId, ruleData);

    yield put({
      type: actionTypes.UPDATE_RULE_SUCCESS,
      payload: response.data
    });

    // Refresh rules list
    yield put({ type: actionTypes.GET_ALL_RULES, payload: {} });
  } catch (error) {
    yield put({
      type: actionTypes.UPDATE_RULE_ERROR,
      payload: error.message || 'Failed to update rule'
    });
  }
}

/**
 * Worker Saga: Delete rule
 */
function* deleteRuleSaga(action) {
  try {
    const { ruleId } = action.payload;
    yield call(dataCategorizationService.deleteRule, ruleId);

    yield put({
      type: actionTypes.DELETE_RULE_SUCCESS,
      payload: ruleId
    });

    // Refresh rules list
    yield put({ type: actionTypes.GET_ALL_RULES, payload: {} });
  } catch (error) {
    yield put({
      type: actionTypes.DELETE_RULE_ERROR,
      payload: error.message || 'Failed to delete rule'
    });
  }
}

/**
 * Worker Saga: Toggle rule status
 */
function* toggleRuleStatusSaga(action) {
  try {
    const { ruleId, enabled } = action.payload;
    const response = yield call(dataCategorizationService.toggleRuleStatus, ruleId, enabled);

    yield put({
      type: actionTypes.TOGGLE_RULE_STATUS_SUCCESS,
      payload: response.data
    });

    // Refresh rules list (consistent with create/update/delete sagas)
    yield put({ type: actionTypes.GET_ALL_RULES, payload: {} });
  } catch (error) {
    yield put({
      type: actionTypes.TOGGLE_RULE_STATUS_ERROR,
      payload: error.message || 'Failed to toggle rule status'
    });
  }
}

// ==================== BATCH PROCESSING SAGAS ====================

/**
 * Worker Saga: Upload batch file
 */
function* uploadBatchFileSaga(action) {
  try {
    console.log('[Saga] Upload batch file started:', action.payload);
    const { file, team, saveResults = true, selectedRuleIds = [] } = action.payload;

    if (!file) {
      throw new Error('No file provided');
    }
    if (!team) {
      throw new Error('No team selected');
    }
    
    console.log('[Saga] Calling uploadBatchFile service...', { 
      fileName: file.name, 
      team, 
      saveResults,
      selectedRuleIds
    });
    
    // Create progress callback
    const onUploadProgress = (percentage) => {
      console.log(`[Saga] Dispatching progress: ${percentage}%`);
      // Dispatch progress action (can't use put in callback, so we'll use a workaround)
      window.dispatchProgress && window.dispatchProgress(percentage);
    };
    
    const response = yield call(dataCategorizationService.uploadBatchFile, file, team, saveResults, onUploadProgress, selectedRuleIds);

    console.log('[Saga] Upload successful:', response.data);

    yield put({
      type: actionTypes.UPLOAD_BATCH_FILE_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    console.error('[Saga] Upload failed:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to upload batch file';
    
    yield put({
      type: actionTypes.UPLOAD_BATCH_FILE_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Get batch status
 */
function* getBatchStatusSaga(action) {
  try {
    const { batchId } = action.payload;
    const response = yield call(dataCategorizationService.getBatchStatus, batchId);

    yield put({
      type: actionTypes.GET_BATCH_STATUS_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_BATCH_STATUS_ERROR,
      payload: error.message || 'Failed to fetch batch status'
    });
  }
}

/**
 * Worker Saga: Cancel batch
 */
function* cancelBatchSaga(action) {
  try {
    const { batchId } = action.payload;
    const response = yield call(dataCategorizationService.cancelBatch, batchId);

    yield put({
      type: actionTypes.CANCEL_BATCH_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.CANCEL_BATCH_ERROR,
      payload: error.message || 'Failed to cancel batch'
    });
  }
}

/**
 * Worker Saga: Get batch results
 */
function* getBatchResultsSaga(action) {
  try {
    const {
      batchId,
      params,
      page,
      size,
      status,
      sortField,
      sortOrder,
      filters
    } = action.payload;
    // Support both { batchId, params: {...} } and individual fields
    const effectiveParams = params || {
      page: page || 0,
      size: size || 50,
      status: status || '',
      sortField: sortField || '',
      sortOrder: sortOrder || 1,
      filters: filters || []
    };
    const response = yield call(dataCategorizationService.getBatchResults, batchId, effectiveParams);

    yield put({
      type: actionTypes.GET_BATCH_RESULTS_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_BATCH_RESULTS_ERROR,
      payload: error.message || 'Failed to fetch batch results'
    });
  }
}

/**
 * Worker Saga: Get recent batches
 */
function* getRecentBatchesSaga(action) {
  try {
    console.log('[Saga] Fetching recent batches...');
    const { limit = 10 } = action.payload || {};
    const response = yield call(dataCategorizationService.getRecentBatches, limit);
    console.log('[Saga] Recent batches fetched successfully:', response.data);

    yield put({
      type: actionTypes.GET_RECENT_BATCHES_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    console.warn('[Saga] Failed to fetch recent batches (non-critical):', error.message);
    // Return empty array on error - this is not critical for the application
    yield put({
      type: actionTypes.GET_RECENT_BATCHES_ERROR,
      payload: error.message || 'Failed to fetch recent batches'
    });
  }
}

// ==================== TEAMS SAGAS ====================

/**
 * Worker Saga: Load teams from backend
 */
function* loadTeamsSaga(action) {
  try {
    console.log('[Saga] LOAD_TEAMS action received, fetching teams from backend...');
    console.log('[Saga] Calling dataCategorizationService.getMyTeams()');

    const response = yield call(dataCategorizationService.getMyTeams);

    console.log('[Saga] Teams API Response:', response);
    console.log('[Saga] Teams data:', response.data);
    console.log('[Saga] Response status:', response.status);

    // Backend returns List<String> or objects — transform to {label, value} for dropdown
    const teamsData = response.data || [];
    const teams = Array.isArray(teamsData)
      ? teamsData.map(team =>
          typeof team === 'string'
            ? { label: team, value: team }
            : { label: team.label || team.name || team.value, value: team.value || team.name || team.label }
        )
      : [];

    yield put({
      type: actionTypes.LOAD_TEAMS_SUCCESS,
      payload: teams
    });
    
    console.log('[Saga] LOAD_TEAMS_SUCCESS dispatched with teams:', teams);
  } catch (error) {
    console.error('[Saga] Failed to fetch teams - Error:', error);
    console.error('[Saga] Error message:', error.message);
    console.error('[Saga] Error response:', error.response);
    
    yield put({
      type: actionTypes.LOAD_TEAMS_ERROR,
      payload: error.message || 'Failed to fetch teams'
    });
  }
}

// ==================== RESULT VALIDATION SAGAS ====================

/**
 * Worker Saga: Validate result
 */
function* validateResultSaga(action) {
  try {
    const { resultId, validatedCategory } = action.payload;
    const response = yield call(dataCategorizationService.validateResult, resultId, validatedCategory);

    yield put({
      type: actionTypes.VALIDATE_RESULT_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.VALIDATE_RESULT_ERROR,
      payload: error.message || 'Failed to validate result'
    });
  }
}

/**
 * Worker Saga: Bulk validate results
 */
function* bulkValidateResultsSaga(action) {
  try {
    const { resultIds, validatedCategory } = action.payload;
    const response = yield call(dataCategorizationService.bulkValidateResults, resultIds, validatedCategory);

    yield put({
      type: actionTypes.BULK_VALIDATE_RESULTS_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.BULK_VALIDATE_RESULTS_ERROR,
      payload: error.message || 'Failed to bulk validate results'
    });
  }
}

// ==================== EXCEL OPERATIONS SAGAS ====================

/**
 * Worker Saga: Ingest Excel file
 */
function* ingestExcelFileSaga(action) {
  try {
    const { file } = action.payload;
    const response = yield call(dataCategorizationService.ingestExcelFile, file);

    yield put({
      type: actionTypes.INGEST_EXCEL_FILE_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.INGEST_EXCEL_FILE_ERROR,
      payload: error.message || 'Failed to ingest Excel file'
    });
  }
}

/**
 * Worker Saga: Export to Excel
 */
function* exportToExcelSaga(action) {
  try {
    const { status = '' } = action.payload || {};
    const response = yield call(dataCategorizationService.exportToExcel, status);

    // Create blob and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `categorization-export-${Date.now()}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    yield put({
      type: actionTypes.EXPORT_TO_EXCEL_SUCCESS
    });
  } catch (error) {
    yield put({
      type: actionTypes.EXPORT_TO_EXCEL_ERROR,
      payload: error.message || 'Failed to export to Excel'
    });
  }
}

// ==================== DATA ITEMS SAGAS ====================

/**
 * Worker Saga: Get all data items
 */
function* getAllDataItemsSaga(action) {
  try {
    const params = action.payload || {};
    const response = yield call(dataCategorizationService.getAllDataItems, params);

    yield put({
      type: actionTypes.GET_ALL_DATA_ITEMS_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_ALL_DATA_ITEMS_ERROR,
      payload: error.message || 'Failed to fetch data items'
    });
  }
}

/**
 * Worker Saga: Get data item by ID
 */
function* getDataItemByIdSaga(action) {
  try {
    const { dataItemId } = action.payload;
    const response = yield call(dataCategorizationService.getDataItemById, dataItemId);

    yield put({
      type: actionTypes.GET_DATA_ITEM_BY_ID_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.GET_DATA_ITEM_BY_ID_ERROR,
      payload: error.message || 'Failed to fetch data item'
    });
  }
}

/**
 * Worker Saga: Categorize data item
 */
function* categorizeDataItemSaga(action) {
  try {
    const dataItem = action.payload;
    const response = yield call(dataCategorizationService.categorizeDataItem, dataItem);

    yield put({
      type: actionTypes.CATEGORIZE_DATA_ITEM_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.CATEGORIZE_DATA_ITEM_ERROR,
      payload: error.message || 'Failed to categorize data item'
    });
  }
}

/**
 * Worker Saga: Recategorize data item
 */
function* recategorizeDataItemSaga(action) {
  try {
    const { dataItemId } = action.payload;
    const response = yield call(dataCategorizationService.recategorizeDataItem, dataItemId);

    yield put({
      type: actionTypes.RECATEGORIZE_DATA_ITEM_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.RECATEGORIZE_DATA_ITEM_ERROR,
      payload: error.message || 'Failed to recategorize data item'
    });
  }
}

/**
 * Root Saga - Watches all actions and triggers appropriate sagas
// ==================== ATTESTATION SAGAS ====================

/**
 * Worker Saga: Attest single result (accept/deny)
 */
function* attestResultSaga(action) {
  try {
    const request = action.payload;
    const response = yield call(dataCategorizationService.attestResult, request);

    yield put({
      type: actionTypes.ATTEST_RESULT_SUCCESS,
      payload: response.data
    });
  } catch (error) {
    yield put({
      type: actionTypes.ATTEST_RESULT_ERROR,
      payload: error.message || 'Failed to attest result'
    });
  }
}

/**
 * Worker Saga: Bulk attest results
 */
function* bulkAttestResultsSaga(action) {
  try {
    const requests = action.payload;
    const response = yield call(dataCategorizationService.bulkAttestResults, requests);

    yield put({
      type: actionTypes.BULK_ATTEST_RESULTS_SUCCESS,
      payload: {
        ...response.data,
        // Forward the original requests so the reducer can identify which rows were attested
        _requests: requests,
      }
    });
  } catch (error) {
    yield put({
      type: actionTypes.BULK_ATTEST_RESULTS_ERROR,
      payload: error.message || 'Failed to bulk attest results'
    });
  }
}

/**
 * Worker Saga: Re-process batch (ALL or FAILED_ONLY)
 */
function* reprocessBatchSaga(action) {
  try {
    const { batchId, mode } = action.payload;
    console.log('[Saga] Re-processing batch:', batchId, 'mode:', mode);

    const response = yield call(dataCategorizationService.reprocessBatch, batchId, mode);

    yield put({
      type: actionTypes.REPROCESS_BATCH_SUCCESS,
      payload: response.data
    });

    // Refresh batch history
    yield put({
      type: actionTypes.GET_RECENT_BATCHES,
      payload: { limit: 10 }
    });
  } catch (error) {
    console.error('[Saga] Re-process batch failed:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to re-process batch';
    yield put({
      type: actionTypes.REPROCESS_BATCH_ERROR,
      payload: errorMessage
    });
  }
}

/**
 * Worker Saga: Accept ALL categorized results for a batch (server-side)
 * Calls POST /processing/batch/{batchId}/accept-all — no pagination needed.
 */
function* acceptAllBatchSaga(action) {
  try {
    const { batchId } = action.payload;
    const response = yield call(dataCategorizationService.acceptAllBatch, batchId);

    yield put({
      type: actionTypes.ACCEPT_ALL_BATCH_SUCCESS,
      payload: { ...response.data, batchId }
    });
  } catch (error) {
    yield put({
      type: actionTypes.ACCEPT_ALL_BATCH_ERROR,
      payload: error.message || 'Failed to accept all results'
    });
  }
}

// ==================== TEAM MANAGEMENT SAGAS ====================

/**
 * Worker Saga: Get all teams with details
 */
function* getAllTeamsDetailedSaga() {
  try {
    const response = yield call(dataCategorizationService.getMyAdminTeams);
    yield put({ type: actionTypes.GET_ALL_TEAMS_DETAILED_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: actionTypes.GET_ALL_TEAMS_DETAILED_ERROR, payload: error.message || 'Failed to fetch teams' });
  }
}

/**
 * Worker Saga: Create team
 */
function* createTeamSaga(action) {
  try {
    const response = yield call(dataCategorizationService.createTeam, action.payload);
    yield put({ type: actionTypes.CREATE_TEAM_SUCCESS, payload: response.data });
    // Refresh teams list
    yield put({ type: actionTypes.LOAD_TEAMS });
    yield put({ type: actionTypes.GET_ALL_TEAMS_DETAILED });
  } catch (error) {
    yield put({ type: actionTypes.CREATE_TEAM_ERROR, payload: error.message || 'Failed to create team' });
  }
}

/**
 * Worker Saga: Delete team
 */
function* deleteTeamSaga(action) {
  try {
    const { teamId } = action.payload;
    yield call(dataCategorizationService.deleteTeam, teamId);
    yield put({ type: actionTypes.DELETE_TEAM_SUCCESS, payload: { teamId } });
    // Refresh teams list
    yield put({ type: actionTypes.LOAD_TEAMS });
    yield put({ type: actionTypes.GET_ALL_TEAMS_DETAILED });
  } catch (error) {
    yield put({ type: actionTypes.DELETE_TEAM_ERROR, payload: error.message || 'Failed to delete team' });
  }
}

/**
 * Worker Saga: Add team member
 */
function* addTeamMemberSaga(action) {
  try {
    const { teamId, memberData } = action.payload;
    const response = yield call(dataCategorizationService.addTeamMember, teamId, memberData);
    yield put({ type: actionTypes.ADD_TEAM_MEMBER_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: actionTypes.ADD_TEAM_MEMBER_ERROR, payload: error.message || 'Failed to add team member' });
  }
}

/**
 * Worker Saga: Remove team member
 */
function* removeTeamMemberSaga(action) {
  try {
    const { teamId, memberUserId } = action.payload;
    const response = yield call(dataCategorizationService.removeTeamMember, teamId, memberUserId);
    yield put({ type: actionTypes.REMOVE_TEAM_MEMBER_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: actionTypes.REMOVE_TEAM_MEMBER_ERROR, payload: error.message || 'Failed to remove team member' });
  }
}

/**
 * Worker Saga: Get my teams
 */
function* getMyTeamsSaga() {
  try {
    const response = yield call(dataCategorizationService.getMyTeams);
    yield put({ type: actionTypes.GET_MY_TEAMS_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: actionTypes.GET_MY_TEAMS_ERROR, payload: error.message || 'Failed to fetch my teams' });
  }
}

/**
 * Worker Saga: Check team access
 */
function* checkTeamAccessSaga(action) {
  try {
    const { teamName } = action.payload;
    const response = yield call(dataCategorizationService.checkTeamAccess, teamName);
    yield put({ type: actionTypes.CHECK_TEAM_ACCESS_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: actionTypes.CHECK_TEAM_ACCESS_ERROR, payload: error.message || 'Failed to check team access' });
  }
}

/**
 * Root Saga - Watches all actions and triggers appropriate sagas
 */
export function* dataCategorySaga() {
  console.log('[Saga] Data Category Saga initialized - watchers active');
  
  // Categories watchers
  yield takeEvery(actionTypes.GET_ALL_DATA_CATEGORIES, fetchAllCategoriesSaga);
  yield takeEvery(actionTypes.GET_DATA_CATEGORY, fetchCategoryByIdSaga);
  yield takeEvery(actionTypes.CREATE_DATA_CATEGORY, createCategorySaga);
  yield takeEvery(actionTypes.UPDATE_DATA_CATEGORY, updateCategorySaga);
  yield takeEvery(actionTypes.DELETE_DATA_CATEGORY, deleteCategorySaga);
  yield takeEvery(actionTypes.CHANGE_CATEGORY_STATUS, changeStatusSaga);
  yield takeEvery(actionTypes.SEARCH_CATEGORIES, searchCategoriesSaga);
  yield takeEvery(actionTypes.GET_AUDIT_LOG, getAuditLogSaga);

  // Rules Management watchers
  yield takeLatest(actionTypes.GET_ALL_RULES, getAllRulesSaga);
  yield takeLatest(actionTypes.GET_RULE_BY_ID, getRuleByIdSaga);
  yield takeEvery(actionTypes.CREATE_RULE, createRuleSaga);
  yield takeEvery(actionTypes.UPDATE_RULE, updateRuleSaga);
  yield takeEvery(actionTypes.DELETE_RULE, deleteRuleSaga);
  yield takeEvery(actionTypes.TOGGLE_RULE_STATUS, toggleRuleStatusSaga);

  // Batch Processing watchers
  console.log('[Saga] Setting up batch processing watchers...');
  console.log('[Saga] UPLOAD_BATCH_FILE action type:', actionTypes.UPLOAD_BATCH_FILE);
  yield takeEvery(actionTypes.UPLOAD_BATCH_FILE, uploadBatchFileSaga);
  console.log('[Saga] Upload batch file watcher registered');
  yield takeLatest(actionTypes.GET_BATCH_STATUS, getBatchStatusSaga);
  yield takeEvery(actionTypes.CANCEL_BATCH, cancelBatchSaga);
  yield takeLatest(actionTypes.GET_BATCH_RESULTS, getBatchResultsSaga);
  yield takeLatest(actionTypes.GET_RECENT_BATCHES, getRecentBatchesSaga);
  yield takeEvery(actionTypes.REPROCESS_BATCH, reprocessBatchSaga);

  // Result Validation watchers
  yield takeEvery(actionTypes.VALIDATE_RESULT, validateResultSaga);
  yield takeEvery(actionTypes.BULK_VALIDATE_RESULTS, bulkValidateResultsSaga);

  // Excel Operations watchers
  yield takeEvery(actionTypes.INGEST_EXCEL_FILE, ingestExcelFileSaga);
  yield takeEvery(actionTypes.EXPORT_TO_EXCEL, exportToExcelSaga);

  // Data Items watchers
  yield takeLatest(actionTypes.GET_ALL_DATA_ITEMS, getAllDataItemsSaga);
  yield takeLatest(actionTypes.GET_DATA_ITEM_BY_ID, getDataItemByIdSaga);
  yield takeEvery(actionTypes.CATEGORIZE_DATA_ITEM, categorizeDataItemSaga);
  yield takeEvery(actionTypes.RECATEGORIZE_DATA_ITEM, recategorizeDataItemSaga);

  // Teams watcher
  yield takeEvery(actionTypes.LOAD_TEAMS, loadTeamsSaga);

  // Team Management watchers
  yield takeLatest(actionTypes.GET_ALL_TEAMS_DETAILED, getAllTeamsDetailedSaga);
  yield takeEvery(actionTypes.CREATE_TEAM, createTeamSaga);
  yield takeEvery(actionTypes.DELETE_TEAM, deleteTeamSaga);
  yield takeEvery(actionTypes.ADD_TEAM_MEMBER, addTeamMemberSaga);
  yield takeEvery(actionTypes.REMOVE_TEAM_MEMBER, removeTeamMemberSaga);
  yield takeLatest(actionTypes.GET_MY_TEAMS, getMyTeamsSaga);
  yield takeLatest(actionTypes.CHECK_TEAM_ACCESS, checkTeamAccessSaga);

  // Attestation watchers
  yield takeEvery(actionTypes.ATTEST_RESULT, attestResultSaga);
  yield takeEvery(actionTypes.BULK_ATTEST_RESULTS, bulkAttestResultsSaga);
  yield takeEvery(actionTypes.ACCEPT_ALL_BATCH, acceptAllBatchSaga);
}

export default dataCategorySaga;
